import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/logo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { signIn, signUp, type SignUpData } from '@/lib/auth';
import { RegistrationConfirmation } from './registration-confirmation';
import { ResetPasswordDialog } from './reset-password-dialog';
import { useProfile } from '@/lib/providers';
import { supabase } from '@/lib/supabase';

import { supportEmail } from '@/lib/constants';

// this value also depends on the logic in the handle_new_user trigger in the database
const INVITE_CODE_REQUIRED = true;

export function AuthForm({ onModeChange }: { onModeChange?: (isLogin: boolean) => void }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteParam = searchParams.get('invite');
  const signupParam = searchParams.get('signup');

  const { refreshProfile } = useProfile();

  // if provided in URL, we prevent the user from changing the invite code (until they try to register once)
  const [canChangeInviteCode, setCanChangeInviteCode] = useState(!inviteParam);

  const [isLogin, setIsLogin] = useState(signupParam !== '1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    // get invite code (even if input is disabled)
    const curInviteCode = isLogin
      ? null
      : (e.currentTarget.querySelector('#inviteCode') as HTMLInputElement)
          .value;

    try {
      if (isLogin) {
        const data = await signIn({ email, password });

        // Reject hoofdhartuser accounts on the Hartzorg login (this variant's
        // /login is Hartzorg-only). Admins pass through.
        const { data: roleRow } = await supabase
          .from('profile_role')
          .select('role')
          .eq('user_id', data.user.id)
          .maybeSingle();
        if (roleRow?.role === 'hoofdhartuser') {
          await supabase.auth.signOut();
          setError(
            'Dit account hoort bij de HoofdnaarHart app. Gebruik de HoofdnaarHart app om in te loggen.',
          );
          setLoading(false);
          return;
        }

        // refresh profile in provider, so we don't have to do a full page refresh
        refreshProfile();
        navigate('/');
      } else {
        const confirmPassword = formData.get('confirmPassword') as string;

        // Check if passwords match
        if (password !== confirmPassword) {
          setError('Wachtwoorden komen niet overeen');
          setLoading(false);
          return;
        }

        const signUpData: SignUpData = {
          email,
          password,
          naam: formData.get('naam') as string,
          telefoonnummer: (formData.get('telefoonnummer') as string) || null,
          inviteCode: curInviteCode,
        };
        await signUp(signUpData);

        // Show confirmation dialog
        refreshProfile();
        setRegisteredEmail(email);
        setShowConfirmation(true);
        setLoading(false);
        return;
      }
    } catch (err) {
      if (curInviteCode && err.message === 'Database error saving new user') {
        setError(
          `De uitnodigingscode is mischien ongeldig of verlopen. Neem contact op via ${supportEmail}`,
        );
      } else {
        console.log(`error: '${err.message}'`);
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setCanChangeInviteCode(true);
    }
  }

  const isCapacitor = !!(window as any).Capacitor;
  const hideLogo = isCapacitor && !isLogin;

  const RequiredStar = isLogin ? null : <span className="text-red-500">*</span>;

  return (
    <>
      <RegistrationConfirmation
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setIsLogin(true);
        }}
        email={registeredEmail}
      />
      <ResetPasswordDialog
        open={showResetPassword}
        onClose={() => setShowResetPassword(false)}
      />
      <Card className="w-full max-w-md p-6 space-y-6 bg-white/80 backdrop-blur-md shadow-xl text-slate-900">
        {/* logotrans.svg rather than the default logo.svg: that one carries an
            opaque white box, which shows as a patch on the translucent card */}
        {/* larger than the HoofdnaarHart logo's w-48 on purpose: this file
            carries much more empty space around its artwork, so at the same
            box size it renders noticeably smaller */}
        {!hideLogo && (
          <Logo className="w-64 h-64 mx-auto" src="/images/logotrans.svg" />
        )}

        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-bold">
            {isLogin ? 'Inloggen' : 'Registreren'}
          </h2>
          <p className="text-slate-600">
            {isLogin ? 'Welkom terug' : 'Maak je account aan'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div className="space-y-2">
                <Label htmlFor="inviteCode">
                  Uitnodigingscode{' '}
                  {INVITE_CODE_REQUIRED ? (
                    <span className="text-red-500">*</span>
                  ) : null}
                </Label>
                <Input
                  id="inviteCode"
                  name="inviteCode"
                  type="text"
                  placeholder="Voer je uitnodigingscode in"
                  defaultValue={inviteParam || ''}
                  disabled={!canChangeInviteCode}
                  required={INVITE_CODE_REQUIRED}
                  autoFocus={canChangeInviteCode}
                />
              </div>
              <hr className="my-4 border-gray-300" />
              <div className="space-y-2">
                <Label htmlFor="naam">Naam {RequiredStar}</Label>
                <Input
                  id="naam"
                  name="naam"
                  required
                  autoFocus={!canChangeInviteCode}
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email {RequiredStar}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              autoFocus={isLogin}
            />
          </div>

          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="telefoonnummer">Telefoonnummer</Label>
              <Input id="telefoonnummer" name="telefoonnummer" type="tel" />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord {RequiredStar}</Label>
            <Input id="password" name="password" type="password" required />
          </div>

          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                Bevestig wachtwoord <span className="text-red-500">*</span>
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
              />
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}

          <Button
            type="submit"
            className="w-full rounded-full bg-green-800 hover:bg-green-700 text-white"
            disabled={loading}
          >
            {loading ? 'Laden...' : isLogin ? 'Inloggen' : 'Registreren'}
          </Button>
        </form>

        <div className="text-center space-y-2">
          <Button
            variant="link"
            onClick={() => { const next = !isLogin; setIsLogin(next); onModeChange?.(next); }}
            className="text-sm text-slate-900"
          >
            {isLogin
              ? 'Nog geen account? Registreer je'
              : 'Al een account? Log in'}
          </Button>
          {isLogin && (
            <div className="space-y-2">
              <Button
                variant="link"
                onClick={() => setShowResetPassword(true)}
                className="text-sm text-slate-900"
              >
                Wachtwoord vergeten?
              </Button>
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
